import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Popper } from "@mui/material";
import {
  KeyboardEvent,
  MouseEvent,
  Touch,
  TouchEvent,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  WheelEvent,
} from "react";
import { ContextProvider } from "../../../contextProvider";
import strings from "../../../strings";
import { TypeUserData } from "../../../types/TypeUsers";
import { getTranslation } from "../../../utils/getTranslation";
import CircleAvatar from "../../CircleAvatar/CircleAvatar";
import s from "./TextChatMessage.module.css";
import {
  TextChatMessageAction,
  TextChatMessageShellProps,
} from "./TextChatMessage.types";

type MessageReaction = {
  emoji: string;
  reactBy: Pick<TypeUserData, "name" | "surname" | "_id">;
};

type GroupedReaction = {
  emoji: string;
  reactBy: Pick<TypeUserData, "name" | "surname" | "_id">[];
  count: number;
};

const DEFAULT_REACTION_EMOJI = [
  "\u{1F44D}",
  "\u{1F44E}",
  "\u{1F525}",
  "\u{1F602}",
  "\u{2764}\u{FE0F}",
  "\u{1F44F}",
  "\u{1F62E}",
  "\u{1F680}",
  "\u{1F389}",
];
const REACTION_TRIGGER_EMOJI = "\u{2764}\u{FE0F}";
const INLINE_REACTION_EMOJI = [
  ...DEFAULT_REACTION_EMOJI.filter((emoji) => emoji !== REACTION_TRIGGER_EMOJI),
  REACTION_TRIGGER_EMOJI,
];

const groupReactionsByEmoji = (
  reactions: MessageReaction[],
): GroupedReaction[] => {
  const grouped = reactions.reduce<
    Record<string, Pick<TypeUserData, "name" | "surname" | "_id">[]>
  >((acc, reaction) => {
    const existingUsers = acc[reaction.emoji] ?? [];

    if (existingUsers.some((user) => user._id === reaction.reactBy?._id)) {
      return acc;
    }

    return {
      ...acc,
      [reaction.emoji]: [...existingUsers, reaction.reactBy],
    };
  }, {});

  return Object.entries(grouped).map(([emoji, reactBy]) => ({
    emoji,
    reactBy,
    count: reactBy.length,
  }));
};

const hasUserReaction = (
  reactions: MessageReaction[],
  emoji: string,
  userId?: string,
) =>
  Boolean(userId) &&
  reactions.some(
    (reaction) => reaction.emoji === emoji && reaction.reactBy?._id === userId,
  );

const toggleUserReaction = (
  reactions: MessageReaction[],
  emoji: string,
  reactBy?: Pick<TypeUserData, "name" | "surname" | "_id">,
) => {
  if (!reactBy?._id) {
    return reactions;
  }

  if (hasUserReaction(reactions, emoji, reactBy._id)) {
    return reactions.filter(
      (reaction) =>
        !(reaction.emoji === emoji && reaction.reactBy?._id === reactBy._id),
    );
  }

  return [...reactions, { emoji, reactBy }];
};

const formatTime = (date: Date) => {
  const hours = new Date(date).getHours().toString().padStart(2, "0");
  const minutes = new Date(date).getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getDeliveryStateLabel = (
  deliveryState: TextChatMessageShellProps["deliveryState"],
) => {
  switch (deliveryState) {
    case "pending":
    case "queued":
      return strings.pendingStatus;
    case "retrying":
      return strings.sending;
    case "failed":
      return strings.failedStatus;
    default:
      return deliveryState;
  }
};

const LONG_PRESS_DELAY_MS = 550;
const LONG_PRESS_MOVE_THRESHOLD_PX = 12;

const createVirtualAnchor = (x: number, y: number) => ({
  getBoundingClientRect: () =>
    ({
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x,
      x,
      y,
      toJSON: () => undefined,
    }) as DOMRect,
});

const ActionMenu = ({
  actions,
  canReact,
  anchorEl,
  isOpen,
  onClose,
  onReact,
}: {
  actions?: TextChatMessageAction[];
  canReact: boolean;
  anchorEl: HTMLElement | ReturnType<typeof createVirtualAnchor> | null;
  isOpen: boolean;
  onClose: () => void;
  onReact: (emoji: string) => void;
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const contextReactionRowRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  const visibleActions = (actions || []).filter(
    (action) => action.id !== "react",
  );

  const closeMenu = () => {
    onClose();
  };

  const handleContextReactionWheel = (event: WheelEvent<HTMLDivElement>) => {
    const row = contextReactionRowRef.current;
    if (!row) {
      return;
    }

    const maxScrollLeft = row.scrollWidth - row.clientWidth;
    if (maxScrollLeft <= 0) {
      return;
    }

    const dominantDelta =
      Math.abs(event.deltaX) >= Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
    if (!dominantDelta) {
      return;
    }

    const nextScrollLeft = Math.min(
      Math.max(row.scrollLeft + dominantDelta, 0),
      maxScrollLeft,
    );
    const canScrollInDirection =
      dominantDelta < 0 ? row.scrollLeft > 0 : row.scrollLeft < maxScrollLeft;

    if (!canScrollInDirection) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    row.scrollLeft = nextScrollLeft;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const clickOutsideHandler = (event: globalThis.MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", clickOutsideHandler);
    return () => document.removeEventListener("mousedown", clickOutsideHandler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      firstItemRef.current?.focus();
    }
  }, [isOpen]);

  if (!visibleActions.length && !canReact) {
    return null;
  }

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "viewport",
            padding: 8,
          },
        },
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: ["top-start", "bottom-end", "top-end"],
          },
        },
      ]}
      style={{ zIndex: 9999 }}
    >
      {isOpen && (
        <div
          id={menuId}
          className={s.actionMenuSurface}
          data-testid="text-chat-action-menu"
          ref={menuRef}
          role="menu"
          aria-label={strings.globalChatMessageActions}
          onKeyDown={(event) => {
            const items = Array.from(
              event.currentTarget.querySelectorAll<HTMLButtonElement>(
                "[role='menuitem']:not(:disabled)",
              ),
            );
            const currentIndex = items.indexOf(
              document.activeElement as HTMLButtonElement,
            );

            if (event.key === "Escape") {
              event.preventDefault();
              closeMenu();
              return;
            }

            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              const direction = event.key === "ArrowDown" ? 1 : -1;
              const nextIndex =
                currentIndex >= 0
                  ? (currentIndex + direction + items.length) % items.length
                  : 0;
              items[nextIndex]?.focus();
            }
          }}
        >
          {canReact && (
            <div
              ref={contextReactionRowRef}
              className={s.contextReactionRow}
              data-testid="text-chat-context-reactions"
              aria-label={strings.globalChatQuickReactions}
              onWheel={handleContextReactionWheel}
            >
              {DEFAULT_REACTION_EMOJI.map((emoji) => (
                <button
                  ref={
                    !visibleActions.length &&
                    emoji === DEFAULT_REACTION_EMOJI[0]
                      ? firstItemRef
                      : undefined
                  }
                  type="button"
                  role="menuitem"
                  key={emoji}
                  className={s.contextReactionButton}
                  data-prevent-reaction-picker="true"
                  aria-label={
                    strings.formatString("globalChatReactWith", {
                      emoji,
                    }) as string
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onReact(emoji);
                    onClose();
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
          {visibleActions.length > 0 && (
            <div className={s.actionMenuList}>
              {visibleActions.map((action, index) => (
                <button
                  ref={index === 0 ? firstItemRef : undefined}
                  type="button"
                  role="menuitem"
                  key={action.id}
                  autoFocus={index === 0}
                  disabled={action.disabled}
                  data-prevent-reaction-picker="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClose();
                    action.onSelect();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Popper>
  );
};

const TextChatMessageShell = ({
  message,
  body,
  actions,
  style,
  reactOnMessage,
  isDarkChat,
  isChatForVideoSession,
  groupChatMembers,
  deliveryState,
  focusTargetId,
  highlighted,
}: TextChatMessageShellProps) => {
  const { userData } = useContext(ContextProvider);
  const currentUserId = userData?._id;
  const currentUserReactionData = useMemo(
    () =>
      userData?._id
        ? {
            _id: userData._id,
            name: userData.name,
            surname: userData.surname,
          }
        : undefined,
    [userData?._id, userData?.name, userData?.surname],
  );
  const isUserMessage = message.senderKind === "currentUser";
  const messageDeliveryState = deliveryState || message.deliveryState;
  const isLocalOnlyMessage = Boolean(message.isLocalOnly);
  const canReactOnMessage =
    Boolean(reactOnMessage) &&
    Boolean(currentUserId) &&
    !isLocalOnlyMessage &&
    message.userId !== "System" &&
    message.type !== "system";
  const [optimisticReactions, setOptimisticReactions] = useState<
    MessageReaction[]
  >(message.reactions ?? []);
  const [isInlineReactionPaletteOpen, setIsInlineReactionPaletteOpen] =
    useState(false);
  const [isReactionUsersPopoverOpen, setIsReactionUsersPopoverOpen] =
    useState(false);
  const [isReadUsersPopoverOpen, setIsReadUsersPopoverOpen] = useState(false);
  const [selectedReactionEmoji, setSelectedReactionEmoji] = useState<
    string | null
  >(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [reactionUsersAnchorEl, setReactionUsersAnchorEl] =
    useState<HTMLElement | null>(null);
  const [readUsersAnchorEl, setReadUsersAnchorEl] =
    useState<HTMLElement | null>(null);
  const reactionsRef = useRef<HTMLDivElement | null>(null);
  const messageRootRef = useRef<HTMLDivElement | null>(null);
  const reactionUsersPopoverRef = useRef<HTMLDivElement | null>(null);
  const readUsersPopoverRef = useRef<HTMLDivElement | null>(null);
  const reactionLongPressTimeoutRef = useRef<number | null>(null);
  const ignoreNextMessageTapRef = useRef(false);

  const groupedReactions = useMemo(
    () => groupReactionsByEmoji(optimisticReactions),
    [optimisticReactions],
  );
  const isReadByAnotherUser = (message.readBy ?? []).some(
    (userId) => userId !== currentUserId && userId !== message.userId,
  );
  const shouldShowReadStatus =
    isUserMessage &&
    !isLocalOnlyMessage &&
    message.userId !== "System" &&
    message.type !== "booking" &&
    !isChatForVideoSession;
  const readUsers = useMemo(() => {
    const readBySet = new Set(message.readBy ?? []);

    return (groupChatMembers ?? []).filter(
      (member) => member._id !== message.userId && readBySet.has(member._id),
    );
  }, [groupChatMembers, message.readBy, message.userId]);
  const canShowReadUsersPopover =
    shouldShowReadStatus &&
    isReadByAnotherUser &&
    Boolean(groupChatMembers?.length) &&
    readUsers.length > 0;
  const selectedReactionUsers = useMemo(() => {
    if (!selectedReactionEmoji) {
      return [];
    }

    return (
      groupedReactions.find((item) => item.emoji === selectedReactionEmoji)
        ?.reactBy ?? []
    );
  }, [groupedReactions, selectedReactionEmoji]);
  const readStatusIcon = isReadByAnotherUser ? (
    <DoneAllIcon className={s.readStatusIcon} />
  ) : (
    <CheckIcon className={s.readStatusIcon} />
  );
  const canOpenActionMenu =
    message.userId !== "System" &&
    message.type !== "system" &&
    Boolean(actions?.filter((action) => action.id !== "react").length);
  const wasEdited = Boolean(
    message.editedAt || message.updatedAt || message.isEdited,
  );
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<
    HTMLElement | ReturnType<typeof createVirtualAnchor> | null
  >(null);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionLongPressTimeoutRef = useRef<number | null>(null);
  const actionLongPressStartRef = useRef<{ x: number; y: number } | null>(null);

  const closeReactionPopups = () => {
    setIsInlineReactionPaletteOpen(false);
    setIsReactionUsersPopoverOpen(false);
    setIsReadUsersPopoverOpen(false);
    setSelectedReactionEmoji(null);
    setReactionUsersAnchorEl(null);
    setReadUsersAnchorEl(null);
  };

  const toggleReactionWithOptimisticUpdate = async (emoji: string) => {
    if (!canReactOnMessage) {
      return;
    }

    const previousReactions = optimisticReactions;
    setOptimisticReactions(
      toggleUserReaction(previousReactions, emoji, currentUserReactionData),
    );
    setSelectedReactionEmoji(emoji);

    try {
      await reactOnMessage?.(emoji, message.id);
    } catch {
      setOptimisticReactions(previousReactions);
    }
  };

  const handleMessageTap = (event: MouseEvent<HTMLDivElement>) => {
    if (!isTouchDevice || !canReactOnMessage) {
      return;
    }

    if (ignoreNextMessageTapRef.current) {
      ignoreNextMessageTapRef.current = false;
      return;
    }

    const target = event.target as HTMLElement;
    if (
      target.closest('[data-prevent-reaction-picker="true"]') ||
      target.closest("button, a, input, textarea, select, label, img")
    ) {
      return;
    }

    setIsInlineReactionPaletteOpen((prev) => !prev);
  };

  const handleMessageKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      canOpenActionMenu &&
      (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10"))
    ) {
      event.preventDefault();
      setActionMenuAnchorEl(messageRootRef.current);
      setIsActionMenuOpen(true);
      return;
    }

    if (!canReactOnMessage || event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    setIsInlineReactionPaletteOpen((prev) => !prev);
  };

  const closeActionMenu = () => {
    setIsActionMenuOpen(false);
    setActionMenuAnchorEl(null);
  };

  const openActionMenuAtPoint = (x: number, y: number) => {
    if (!canOpenActionMenu) {
      return;
    }

    closeReactionPopups();
    setActionMenuAnchorEl(createVirtualAnchor(x, y));
    setIsActionMenuOpen(true);
  };

  const cancelActionLongPress = () => {
    if (actionLongPressTimeoutRef.current) {
      window.clearTimeout(actionLongPressTimeoutRef.current);
      actionLongPressTimeoutRef.current = null;
    }
    actionLongPressStartRef.current = null;
  };

  const getTouchPoint = (touch: Touch) => ({
    x: touch.clientX,
    y: touch.clientY,
  });

  const handleMessageContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!canOpenActionMenu) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, textarea, select, label")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openActionMenuAtPoint(event.clientX, event.clientY);
  };

  const handleMessageTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!canOpenActionMenu || event.touches.length !== 1) {
      return;
    }

    const target = event.target as HTMLElement;
    if (
      target.closest('[data-prevent-reaction-picker="true"]') ||
      target.closest("button, a, input, textarea, select, label")
    ) {
      return;
    }

    const point = getTouchPoint(event.touches[0]);
    actionLongPressStartRef.current = point;
    actionLongPressTimeoutRef.current = window.setTimeout(() => {
      ignoreNextMessageTapRef.current = true;
      openActionMenuAtPoint(point.x, point.y);
      actionLongPressTimeoutRef.current = null;
    }, LONG_PRESS_DELAY_MS);
  };

  const handleMessageTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const startPoint = actionLongPressStartRef.current;
    if (!startPoint || event.touches.length !== 1) {
      cancelActionLongPress();
      return;
    }

    const point = getTouchPoint(event.touches[0]);
    const distance = Math.hypot(point.x - startPoint.x, point.y - startPoint.y);

    if (distance > LONG_PRESS_MOVE_THRESHOLD_PX) {
      cancelActionLongPress();
    }
  };

  const handleMessageTouchEnd = () => {
    cancelActionLongPress();
  };

  const handleReactionTouchStart = (
    emoji: string,
    event: TouchEvent<HTMLButtonElement>,
  ) => {
    if (!isTouchDevice) {
      return;
    }

    const target = event.currentTarget;
    reactionLongPressTimeoutRef.current = window.setTimeout(() => {
      setIsReactionUsersPopoverOpen(true);
      setReactionUsersAnchorEl(target);
      setSelectedReactionEmoji(emoji);
    }, 450);
  };

  const handleReactionTouchEnd = () => {
    if (reactionLongPressTimeoutRef.current) {
      window.clearTimeout(reactionLongPressTimeoutRef.current);
      reactionLongPressTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    setOptimisticReactions(message.reactions ?? []);
  }, [message.reactions]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    const updateTouchMode = () => setIsTouchDevice(mediaQuery.matches);
    updateTouchMode();
    mediaQuery.addEventListener("change", updateTouchMode);

    return () => mediaQuery.removeEventListener("change", updateTouchMode);
  }, []);

  useEffect(() => {
    const clickOutsideHandler = (event: globalThis.MouseEvent) => {
      const targetNode = event.target as Node;

      if (
        !reactionsRef.current?.contains(targetNode) &&
        !reactionUsersPopoverRef.current?.contains(targetNode) &&
        !readUsersPopoverRef.current?.contains(targetNode) &&
        !readUsersAnchorEl?.contains(targetNode)
      ) {
        closeReactionPopups();
      }
    };

    document.addEventListener("mousedown", clickOutsideHandler);
    return () => document.removeEventListener("mousedown", clickOutsideHandler);
  }, [readUsersAnchorEl]);

  useEffect(() => {
    return () => {
      if (reactionLongPressTimeoutRef.current) {
        window.clearTimeout(reactionLongPressTimeoutRef.current);
      }
      cancelActionLongPress();
    };
  }, []);

  const messageWrapperClassName =
    message.type === "booking" || message.userId === "System"
      ? s.messageRowCenter
      : isUserMessage
        ? s.messageRowUser
        : s.messageRowOther;
  const baseMessageBubbleClassName =
    message.type === "booking"
      ? s.sessionMessage
      : message.userId === "System" || message.type === "system"
        ? s.systemMessage
        : isUserMessage
          ? s.userMessage
          : s.otherMessage;
  const messageBubbleClassName = highlighted
    ? `${baseMessageBubbleClassName} ${s.highlightedMessage}`
    : baseMessageBubbleClassName;

  return (
    <div
      className={messageWrapperClassName}
      data-testid="text-chat-message-shell"
    >
      <div
        ref={messageRootRef}
        id={focusTargetId}
        data-message-id={message.id}
        data-message-type={message.type}
        data-highlighted={highlighted ? "true" : undefined}
        data-delivery-state={deliveryState || message.deliveryState || "sent"}
        className={messageBubbleClassName}
        style={
          isUserMessage && message.type !== "booking"
            ? style?.myMessageBg
            : style?.otherMessage
        }
        onClick={handleMessageTap}
        onContextMenu={handleMessageContextMenu}
        onTouchStart={handleMessageTouchStart}
        onTouchMove={handleMessageTouchMove}
        onTouchEnd={handleMessageTouchEnd}
        onTouchCancel={handleMessageTouchEnd}
        onKeyDown={handleMessageKeyDown}
        tabIndex={canReactOnMessage ? 0 : -1}
        role="article"
        aria-label={
          canReactOnMessage ? strings.globalChatToggleEmojiPicker : undefined
        }
      >
        <ActionMenu
          actions={actions}
          canReact={canReactOnMessage}
          anchorEl={actionMenuAnchorEl}
          isOpen={isActionMenuOpen}
          onClose={closeActionMenu}
          onReact={(emoji) => {
            void toggleReactionWithOptimisticUpdate(emoji);
          }}
        />
        {body}
        <div
          className={`${s.messageFooter} ${
            isUserMessage ? s.messageFooterOwn : s.messageFooterOther
          } ${isDarkChat ? s.darkReactionsWrapper : ""}`}
        >
          {message.userId !== "System" && message.type !== "booking" && (
            <div className={s.reactionsWrapper} ref={reactionsRef}>
              {(groupedReactions.length > 0 || canReactOnMessage) && (
                <div className={s.reactionsRow}>
                  {groupedReactions.map((reaction) => {
                    const isActive = hasUserReaction(
                      optimisticReactions,
                      reaction.emoji,
                      currentUserId,
                    );

                    return (
                      <button
                        type="button"
                        key={reaction.emoji}
                        className={`${s.reactionPill} ${isActive ? s.reactionPillActive : ""}`}
                        aria-label={
                          strings.formatString("globalChatReactionCount", {
                            emoji: reaction.emoji,
                            count: reaction.count,
                          }) as string
                        }
                        onMouseEnter={(event) => {
                          if (isTouchDevice) {
                            return;
                          }

                          setIsReactionUsersPopoverOpen(true);
                          setReactionUsersAnchorEl(event.currentTarget);
                          setSelectedReactionEmoji(reaction.emoji);
                        }}
                        onMouseLeave={() => {
                          if (isTouchDevice) {
                            return;
                          }

                          setIsReactionUsersPopoverOpen(false);
                          setReactionUsersAnchorEl(null);
                          setSelectedReactionEmoji(null);
                        }}
                        onTouchStart={(event) =>
                          handleReactionTouchStart(reaction.emoji, event)
                        }
                        onTouchEnd={handleReactionTouchEnd}
                        onTouchCancel={handleReactionTouchEnd}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleReactionWithOptimisticUpdate(reaction.emoji);
                        }}
                      >
                        <span>{reaction.emoji}</span>
                        <span>{reaction.count}</span>
                      </button>
                    );
                  })}
                  {canReactOnMessage && !isTouchDevice && (
                    <div
                      className={`${s.addReactionHoverArea} ${s.addReactionHoverAreaVisible}`}
                      data-prevent-reaction-picker="true"
                      onBlur={(event) => {
                        if (
                          !event.currentTarget.contains(
                            event.relatedTarget as Node | null,
                          )
                        ) {
                          setIsInlineReactionPaletteOpen(false);
                        }
                      }}
                    >
                      <div
                        className={`${s.reactionPalette} ${
                          isInlineReactionPaletteOpen
                            ? s.reactionPaletteExpanded
                            : ""
                        }`}
                        onMouseEnter={() =>
                          setIsInlineReactionPaletteOpen(true)
                        }
                        onMouseLeave={() =>
                          setIsInlineReactionPaletteOpen(false)
                        }
                        onFocus={() => setIsInlineReactionPaletteOpen(true)}
                      >
                        {(isInlineReactionPaletteOpen
                          ? INLINE_REACTION_EMOJI
                          : ["\u{2764}\u{FE0F}"]
                        ).map((emoji) => (
                          <button
                            type="button"
                            key={emoji}
                            className={`${s.reactionPaletteItem} ${
                              emoji === REACTION_TRIGGER_EMOJI
                                ? s.reactionPaletteTrigger
                                : s.reactionPaletteExtra
                            }`}
                            aria-label={
                              strings.formatString("globalChatReactWith", {
                                emoji,
                              }) as string
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              void toggleReactionWithOptimisticUpdate(emoji);
                              setIsInlineReactionPaletteOpen(false);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Popper
                open={
                  isReactionUsersPopoverOpen &&
                  Boolean(selectedReactionEmoji) &&
                  selectedReactionUsers.length > 0
                }
                anchorEl={reactionUsersAnchorEl ?? reactionsRef.current}
                placement="top-start"
                style={{ zIndex: 9999 }}
              >
                <div
                  ref={reactionUsersPopoverRef}
                  className={`${s.reactionPopover} ${isDarkChat ? s.darkReactionsPopover : ""}`}
                >
                  <div className={s.reactionUsersList}>
                    <div className={s.reactionUsersTitle}>
                      {selectedReactionEmoji} {selectedReactionUsers.length}
                    </div>
                    {selectedReactionUsers.map((reactBy, index) => (
                      <div key={index} className={s.reactionUserItem}>
                        <CircleAvatar
                          userId={reactBy._id}
                          width="32px"
                          height="32px"
                          fontSize="10px"
                        />
                        <span className={s.reactionUserName}>
                          {`${getTranslation(reactBy.name, userData?.selectedLanguage) ?? ""} ${getTranslation(reactBy.surname, userData?.selectedLanguage) ?? ""}`.trim() ||
                            strings.globalChatUnknownUser}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Popper>
            </div>
          )}
          {message.date && message.userId !== "System" && (
            <div className={s.messageMeta}>
              <span className={s.messageTimeTitle}>
                {formatTime(message.date)}
              </span>
              {wasEdited && (
                <span className={s.editedIndicator}>
                  {strings.globalChatEditedMessage}
                </span>
              )}
              {messageDeliveryState && messageDeliveryState !== "sent" && (
                <span
                  className={s.deliveryState}
                  aria-label={strings.globalChatDeliveryState}
                  role="status"
                  aria-live="polite"
                >
                  {getDeliveryStateLabel(messageDeliveryState)}
                </span>
              )}
              {shouldShowReadStatus && canShowReadUsersPopover && (
                <button
                  type="button"
                  className={`${s.readStatus} ${s.readStatusRead} ${s.readStatusClickable}`}
                  title={strings.readStatus}
                  aria-label={strings.readStatus}
                  onClick={(event) => {
                    event.stopPropagation();
                    setReadUsersAnchorEl(event.currentTarget);
                    setIsReadUsersPopoverOpen((prev) => !prev);
                  }}
                >
                  {readStatusIcon}
                </button>
              )}
              {shouldShowReadStatus && !canShowReadUsersPopover && (
                <span
                  className={`${s.readStatus} ${
                    isReadByAnotherUser ? s.readStatusRead : ""
                  }`}
                  title={strings.readStatus}
                  aria-label={strings.readStatus}
                >
                  {readStatusIcon}
                </span>
              )}
              <Popper
                open={isReadUsersPopoverOpen && readUsers.length > 0}
                anchorEl={readUsersAnchorEl}
                placement="top-end"
                style={{ zIndex: 9999 }}
              >
                <div
                  ref={readUsersPopoverRef}
                  className={`${s.reactionPopover} ${isDarkChat ? s.darkReactionsPopover : ""}`}
                >
                  <div className={s.reactionUsersList}>
                    <div className={s.reactionUsersTitle}>
                      {strings.readStatus} {readUsers.length}
                    </div>
                    {readUsers.map((readBy) => (
                      <div key={readBy._id} className={s.reactionUserItem}>
                        <CircleAvatar
                          userId={readBy._id}
                          width="32px"
                          height="32px"
                          fontSize="10px"
                        />
                        <span className={s.reactionUserName}>
                          {`${getTranslation(readBy.name, userData?.selectedLanguage) ?? ""} ${getTranslation(readBy.surname, userData?.selectedLanguage) ?? ""}`.trim() ||
                            strings.globalChatUnknownUser}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Popper>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextChatMessageShell;
